/**
 * Inline formatting model for a single block's text.
 *
 * The live editing buffer is *plain* text — no markdown tokens ever appear
 * in it — with formatting tracked separately as a list of (start, end, kind)
 * ranges ("marks"). Marks only turn into `<b>`/`<i>`/… tokens at the edges —
 * serializing for persistence, and parsing whatever markdown was persisted
 * before.
 *
 * Ported from teacher-mobile/src/screens/studio/editor/richEditor/marks.ts
 * — kept behavior-identical except `parseInline` and `serializeInline`'s new
 * `legacyDollarToggle` option (see below), needed because web content uses
 * literal `$...$` for real KaTeX math. Both parse and serialize are
 * mode-aware: when `legacyDollarToggle: false`, bare `$` are never treated
 * as formatting markers and never escaped, preserving math delimiters. Only
 * backslashes immediately preceding escape-trigger characters are escaped;
 * LaTeX commands like `\frac` (backslash + ordinary letter) are never escaped.
 */

const KIND_ORDER = ['bold', 'italic', 'underline', 'strike', 'code', 'equation', 'color', 'bg'];

// parseInline's own escape-detector (below) always treats "\<c>" as a
// literal <c> when <c> is one of these five characters, regardless of
// legacyDollarToggle — so ONLY a backslash immediately followed by one of
// them is ever ambiguous on reparse and needs protecting. A backslash
// before an ordinary letter (\frac, \sqrt, \alpha — real LaTeX commands,
// the overwhelmingly common case in this app's content) is never
// ambiguous and must NOT be escaped: blindly escaping every backslash
// would double every LaTeX command's backslash on save, breaking math
// wherever the content is later read by a consumer that doesn't re-parse
// it through this module first (e.g. RenderContent's legacy renderer,
// for content it doesn't detect as using this editor's tokens).
const PARSE_ESCAPE_TRIGGER = /[\\*`~$<]/;
const BARE_TRIGGERS_LEGACY = /[*`~$<]/;
const BARE_TRIGGERS_WEB = /[*`~<]/;

function tokenFor(kind, param) {
  switch (kind) {
    case 'bold': return ['<b>', '</b>'];
    case 'italic': return ['<i>', '</i>'];
    case 'underline': return ['<u>', '</u>'];
    case 'strike': return ['<s>', '</s>'];
    case 'code': return ['<code>', '</code>'];
    case 'equation': return ['<eq>', '</eq>'];
    case 'color': return [`<c:${param}>`, '</c>'];
    case 'bg': return [`<hl:${param}>`, '</hl>'];
    default: throw new Error(`unknown mark kind: ${kind}`);
  }
}

function markKey(m) { return `${m.kind}:${m.param ?? ''}`; }
function escapeInline(s, legacyDollarToggle = true) {
  const bareTriggers = legacyDollarToggle ? BARE_TRIGGERS_LEGACY : BARE_TRIGGERS_WEB;
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '\\') {
      out += (i + 1 < s.length && PARSE_ESCAPE_TRIGGER.test(s[i + 1])) ? '\\\\' : '\\';
      continue;
    }
    out += bareTriggers.test(c) ? '\\' + c : c;
  }
  return out;
}

// ── Web-mode helpers ────────────────────────────────────────────────────────
// Web lesson content is AI-written markdown full of KaTeX math, so web mode
// (legacyDollarToggle: false) differs from mobile's original rules in three
// ways, all aimed at "don't rewrite what the teacher didn't touch":
//  - math spans ($..$, $$..$$, \(..\), \[..\]) and `\$` are copied verbatim —
//    no marker parsing and no escaping inside them;
//  - `*`, `**`, `~~` and `` ` `` only act as markers when they pair up like
//    markdown emphasis (non-space inside, closer exists), so `l * w` stays text;
//  - serialization emits text verbatim whenever that re-parses identically,
//    escaping only as a fallback.

const EDITOR_TAG_AT = /^(?:<\/?(?:b|i|u|s|code|eq)>|<(?:c|hl):[a-z]+>|<\/(?:c|hl)>)/;

function isBlank(c) { return c === undefined || /\s/.test(c); }

function findClosing(s, from, token) {
  let j = s.indexOf(token, from);
  while (j !== -1) {
    if (s[j - 1] !== '\\') return j;
    j = s.indexOf(token, j + 1);
  }
  return -1;
}

/** End index (exclusive) of the math span starting at `i`, or -1. */
function mathSpanEnd(s, i) {
  if (s[i] === '\\' && (s[i + 1] === '(' || s[i + 1] === '[')) {
    const close = s[i + 1] === '(' ? '\\)' : '\\]';
    const j = s.indexOf(close, i + 2);
    return j === -1 ? -1 : j + 2;
  }
  if (s[i] !== '$' || s[i - 1] === '\\') return -1;
  if (s[i + 1] === '$') {
    const j = findClosing(s, i + 2, '$$');
    return j === -1 ? -1 : j + 2;
  }
  const j = findClosing(s, i + 1, '$');
  return j > i + 1 ? j + 1 : -1;
}

/** Can `token` at raw[i] open a mark — non-blank right after it and a
 * plausible closer (non-blank before it) further along? */
function canOpenMarker(raw, i, token) {
  const after = i + token.length;
  if (isBlank(raw[after])) return false;
  let j = raw.indexOf(token, after + 1);
  while (j !== -1) {
    if (!isBlank(raw[j - 1]) && raw[j - 1] !== '\\') return true;
    j = raw.indexOf(token, j + 1);
  }
  return false;
}

function webMarkerAt(rest) {
  if (rest.startsWith('**')) return { token: '**', kind: 'bold' };
  if (rest.startsWith('~~')) return { token: '~~', kind: 'strike' };
  if (rest[0] === '`') return { token: '`', kind: 'code' };
  if (rest[0] === '*') return { token: '*', kind: 'italic' };
  return null;
}

function mathMask(text) {
  const mask = new Array(text.length).fill(false);
  let i = 0;
  while (i < text.length) {
    const end = mathSpanEnd(text, i);
    if (end === -1) { i += 1; continue; }
    for (let k = i; k < end; k++) mask[k] = true;
    i = end;
  }
  return mask;
}

function escapeWebRange(text, start, end, mask) {
  let out = '';
  for (let i = start; i < end; i++) {
    const c = text[i];
    if (mask[i]) { out += c; continue; }
    if (c === '\\') { out += /[\\*`~<]/.test(text[i + 1] ?? '') ? '\\\\' : '\\'; continue; }
    if (c === '*' || c === '`' || c === '~') { out += '\\' + c; continue; }
    if (c === '<' && EDITOR_TAG_AT.test(text.slice(i))) { out += '\\<'; continue; }
    out += c;
  }
  return out;
}

function sameInline(a, b) {
  const key = (ms) => ms.map((m) => `${m.kind}:${m.param ?? ''}:${m.start}:${m.end}`).join('|');
  return a.text === b.text && key(a.marks) === key(b.marks);
}

/** Plain text + (possibly overlapping) format ranges -> a markdown string
 * with properly nested tokens, closing and reopening at crossing
 * boundaries. Literal marker-like characters in the plain text are
 * backslash-escaped so they can't be mistaken for real tokens on reload
 * (web mode: only when emitting them verbatim would not reload identically). */
export function serializeInline(text, marks, { legacyDollarToggle = true } = {}) {
  if (legacyDollarToggle) return buildInline(text, marks, (s, e) => escapeInline(text.slice(s, e), true));

  const verbatim = buildInline(text, marks, (s, e) => text.slice(s, e));
  if (sameInline(parseInline(verbatim, { legacyDollarToggle: false }), { text, marks: normalizeMarks(marks) })) {
    return verbatim;
  }
  const mask = mathMask(text);
  return buildInline(text, marks, (s, e) => escapeWebRange(text, s, e, mask));
}

function buildInline(text, marks, emitText) {
  if (!marks.length) return emitText(0, text.length);

  const cuts = new Set([0, text.length]);
  marks.forEach((m) => { cuts.add(m.start); cuts.add(m.end); });
  const points = Array.from(cuts).sort((a, b) => a - b);

  let out = '';
  let openStack = [];

  for (let i = 0; i < points.length - 1; i++) {
    const segStart = points[i];
    const segEnd = points[i + 1];
    if (segStart >= segEnd) continue;
    const active = marks
      .filter((m) => m.start <= segStart && m.end >= segEnd)
      .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));

    let common = 0;
    while (common < openStack.length && common < active.length && markKey(openStack[common]) === markKey(active[common])) common++;
    for (let k = openStack.length - 1; k >= common; k--) out += tokenFor(openStack[k].kind, openStack[k].param)[1];
    openStack = openStack.slice(0, common);
    for (let k = common; k < active.length; k++) {
      out += tokenFor(active[k].kind, active[k].param)[0];
      openStack.push(active[k]);
    }
    out += emitText(segStart, segEnd);
  }
  for (let k = openStack.length - 1; k >= 0; k--) out += tokenFor(openStack[k].kind, openStack[k].param)[1];
  return out;
}

/**
 * Reverse of serializeInline: markdown -> plain text + format ranges.
 *
 * `legacyDollarToggle` (default true, matching mobile's original behavior):
 * when true, a bare `$` toggles an 'equation' mark — backward compat for
 * markdown mobile wrote before the <eq> token scheme existed. Web MUST pass
 * `false`: nearly all existing web lesson content uses literal `$...$` for
 * real KaTeX math (rendered by RenderContent.jsx's own tokenizer), and the
 * legacy toggle would silently consume those `$` characters as formatting
 * markers, destroying the math.
 */
export function parseInline(raw, { legacyDollarToggle = true } = {}) {
  let text = '';
  const marks = [];
  const active = {};

  const open = (kind, param) => { active[kind] = { start: text.length, param }; };
  const close = (kind) => {
    const a = active[kind];
    if (!a) return;
    if (text.length > a.start) marks.push({ start: a.start, end: text.length, kind, param: a.param });
    delete active[kind];
  };
  const toggle = (kind) => { if (active[kind]) close(kind); else open(kind); };

  const web = !legacyDollarToggle;
  let i = 0;
  while (i < raw.length) {
    if (web) {
      if (raw[i] === '\\' && raw[i + 1] === '$') { text += '\\$'; i += 2; continue; }
      const mathEnd = mathSpanEnd(raw, i);
      if (mathEnd !== -1) { text += raw.slice(i, mathEnd); i = mathEnd; continue; }
    }
    if (raw[i] === '\\' && i + 1 < raw.length && /[\\*`~$<]/.test(raw[i + 1])) {
      text += raw[i + 1];
      i += 2;
      continue;
    }
    const rest = raw.slice(i);
    if (rest.startsWith('</b>')) { close('bold'); i += 4; continue; }
    if (rest.startsWith('<b>')) { open('bold'); i += 3; continue; }
    if (rest.startsWith('</i>')) { close('italic'); i += 4; continue; }
    if (rest.startsWith('<i>')) { open('italic'); i += 3; continue; }
    if (rest.startsWith('</u>')) { close('underline'); i += 4; continue; }
    if (rest.startsWith('<u>')) { open('underline'); i += 3; continue; }
    if (rest.startsWith('</s>')) { close('strike'); i += 4; continue; }
    if (rest.startsWith('<s>')) { open('strike'); i += 3; continue; }
    if (rest.startsWith('</code>')) { close('code'); i += 7; continue; }
    if (rest.startsWith('<code>')) { open('code'); i += 6; continue; }
    if (rest.startsWith('</eq>')) { close('equation'); i += 5; continue; }
    if (rest.startsWith('<eq>')) { open('equation'); i += 4; continue; }
    if (rest.startsWith('</c>')) { close('color'); i += 4; continue; }
    if (rest.startsWith('</hl>')) { close('bg'); i += 5; continue; }
    const cOpen = rest.match(/^<c:([a-z]+)>/);
    if (cOpen) { open('color', cOpen[1]); i += cOpen[0].length; continue; }
    const hlOpen = rest.match(/^<hl:([a-z]+)>/);
    if (hlOpen) { open('bg', hlOpen[1]); i += hlOpen[0].length; continue; }
    // Legacy bare-symbol markers, kept so content saved before this token
    // scheme changed still loads correctly. New writes never emit these.
    if (web) {
      const fence = rest.match(/^`{3,}/);
      if (fence) { text += fence[0]; i += fence[0].length; continue; }
      const marker = webMarkerAt(rest);
      if (marker) {
        const { token, kind } = marker;
        const acts = active[kind] ? !isBlank(raw[i - 1]) : canOpenMarker(raw, i, token);
        if (acts) toggle(kind); else text += token;
        i += token.length;
        continue;
      }
    }
    if (rest.startsWith('**')) { toggle('bold'); i += 2; continue; }
    if (rest.startsWith('~~')) { toggle('strike'); i += 2; continue; }
    if (rest.startsWith('`')) { toggle('code'); i += 1; continue; }
    if (legacyDollarToggle && rest.startsWith('$')) { toggle('equation'); i += 1; continue; }
    if (rest.startsWith('*')) { toggle('italic'); i += 1; continue; }
    text += raw[i];
    i += 1;
  }
  Object.keys(active).forEach(close);
  return { text, marks: normalizeMarks(marks) };
}

/** Merges overlapping/touching marks that share a kind (+ param). */
export function normalizeMarks(marks) {
  const byKey = new Map();
  marks.forEach((m) => {
    if (m.start >= m.end) return;
    const k = markKey(m);
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(m);
  });
  const out = [];
  byKey.forEach((list) => {
    list.sort((a, b) => a.start - b.start);
    let cur = null;
    for (const m of list) {
      if (cur && m.start <= cur.end) cur.end = Math.max(cur.end, m.end);
      else { if (cur) out.push(cur); cur = { ...m }; }
    }
    if (cur) out.push(cur);
  });
  return out.sort((a, b) => a.start - b.start);
}

function isFullyCovered(marks, kind, start, end, param) {
  const relevant = marks
    .filter((m) => m.kind === kind && (param === undefined || m.param === param) && m.end > start && m.start < end)
    .sort((a, b) => a.start - b.start);
  let cursor = start;
  for (const m of relevant) {
    if (m.start > cursor) return false;
    cursor = Math.max(cursor, m.end);
    if (cursor >= end) return true;
  }
  return cursor >= end;
}

/** Clips/removes any marks of `kind` overlapping [start,end). */
export function removeMarkRange(marks, kind, start, end) {
  if (start >= end) return marks;
  const out = [];
  for (const m of marks) {
    if (m.kind !== kind || m.end <= start || m.start >= end) { out.push(m); continue; }
    if (m.start < start) out.push({ ...m, end: start });
    if (m.end > end) out.push({ ...m, start: end });
  }
  return normalizeMarks(out);
}

/** Toggles a mark over [start,end): removes it if the range is already
 * fully covered (matching param, for color/bg), otherwise clips any
 * competing same-kind marks in the range and adds the new one. */
export function toggleMark(marks, kind, start, end, param) {
  if (start >= end) return marks;
  const covered = isFullyCovered(marks, kind, start, end, param);
  const clipped = removeMarkRange(marks, kind, start, end);
  if (covered) return clipped;
  return normalizeMarks([...clipped, { kind, start, end, param }]);
}

/** Repositions marks after a text edit: `removed` characters at `at` were
 * replaced with `inserted` characters. */
export function rebaseMarks(marks, at, removed, inserted) {
  const map = (p) => {
    if (p < at) return p;
    if (p >= at + removed) return p - removed + inserted;
    return at + inserted;
  };
  const out = [];
  for (const m of marks) {
    const start = map(m.start);
    const end = map(m.end);
    if (start < end) out.push({ ...m, start, end });
  }
  return normalizeMarks(out);
}

/** Extracts the marks within [start,end), shifted relative to `start` —
 * used when a block is split into two. */
export function sliceMarks(marks, start, end) {
  const out = [];
  for (const m of marks) {
    const s = Math.max(m.start, start);
    const e = Math.min(m.end, end);
    if (s < e) out.push({ ...m, start: s - start, end: e - start });
  }
  return out;
}

/** Smallest (at, removed, inserted) edit that explains oldText -> newText
 * via common-prefix/common-suffix. */
export function diffText(oldText, newText) {
  const maxStart = Math.min(oldText.length, newText.length);
  let start = 0;
  while (start < maxStart && oldText[start] === newText[start]) start++;
  let oldEnd = oldText.length;
  let newEnd = newText.length;
  while (oldEnd > start && newEnd > start && oldText[oldEnd - 1] === newText[newEnd - 1]) { oldEnd--; newEnd--; }
  return { at: start, removed: oldEnd - start, inserted: newEnd - start };
}
